'use client'

import { Fragment, useMemo } from 'react'

import type { CodeSnippetProps } from '@graftcode/design-system'
import { CodeSnippet, Icon, IconComponent } from '@graftcode/design-system'

import { getLanguageForPM } from '@common/core/utils/technology'

import { NoTechnologyPlaceholder } from '@/core/components/Card/components/NoTechnologyPlaceholder'
import { MethodInfoBlock, methodInfoStyles } from '@/core/components/MethodInfoBlock'
import { MethodParameters } from '@/core/components/MethodParameters'
import { MethodReturns } from '@/core/components/MethodReturns'
import { TypeLink } from '@/core/components/TypeLink'
import { Runtime } from '@/core/enums/runtime.enum'
import { useConstructorExecution } from '@/core/hooks'
import { ClassData, ConstructorData, ParameterData } from '@/core/types/library.type'

import styles from './ConstructorCardContent.module.scss'

export function ConstructorCardContent({
  constructor,
  constructorKey,
  classData,
  runtimeName,
}: {
  constructor: ConstructorData
  constructorKey?: string
  classData: ClassData
  runtimeName: Runtime
}) {
  const {
    code,
    executionLogs,
    valueMap,
    variableCode,
    selectedTechnology,
    snippetTechnology,
    handleValueChange,
    handleRun,
    handleClear,
    handleTryIt,
  } = useConstructorExecution({ constructor, constructorKey, classData, runtimeName })

  const resultKey = constructorKey ?? constructor.name

  const language = useMemo(
    (): string | undefined => (snippetTechnology ? getLanguageForPM(snippetTechnology) : undefined),
    [snippetTechnology]
  )

  const snippetKey = useMemo(
    () => `constructor-${resultKey}-${selectedTechnology || 'none'}-${snippetTechnology || 'none'}`,
    [resultKey, selectedTechnology, snippetTechnology]
  )

  const hasParameters = (constructor?.parameters?.length ?? 0) > 0

  return (
    <div className={styles.method}>
      <MethodInfoBlock title="Constructor signature" variant="row">
        <div className={styles.signatureGrid}>
          <span className={methodInfoStyles.parameterWrapper}>
            <TypeLink typeName={classData.name} />
            <span className={methodInfoStyles.space} />
          </span>
          <span className={styles.callable}>
            {constructor.name}(
            {constructor.parameters.map((parameter: ParameterData, index) => (
              <Fragment key={parameter.name}>
                {index !== 0 && (
                  <>
                    ,
                    <span className={methodInfoStyles.space} />
                  </>
                )}
                <span className={methodInfoStyles.parameterWrapper}>
                  <TypeLink typeName={parameter.type} typeUsage={parameter.typeUsage} />
                  <span className={methodInfoStyles.space} />
                  {parameter.name}
                </span>
              </Fragment>
            ))}
            )
          </span>
        </div>
      </MethodInfoBlock>

      {(hasParameters || classData?.name) && (
        <div className={styles.parametersReturnsSection}>
          {hasParameters && (
            <>
              <MethodParameters parameters={constructor.parameters} />
              <IconComponent size={16} icon={Icon.ArrowRightRegular} />
            </>
          )}
          {classData?.name && (
            <div className={styles.returnsSection}>
              <MethodReturns returns={classData.name} />
            </div>
          )}
        </div>
      )}

      <div className={styles.exampleSection}>
        <CodeSnippet
          key={snippetKey}
          {...({
            className: styles.exampleContent,
            code,
            contentVariant: 'advanced',
            isExampleMode: true,
            language,
            noTechnologyPlaceholder: <NoTechnologyPlaceholder />,
            exampleParameters: constructor.parameters,
            valueMap,
            variableCode,
            logs: executionLogs,
            onExampleRun: handleRun,
            onExampleClear: handleClear,
            onExampleValueChange: handleValueChange,
            onTryItToggle: handleTryIt,
          } as CodeSnippetProps & { onTryItToggle?: () => void })}
        />
      </div>
    </div>
  )
}
